import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Autocomplete } from "@material-ui/lab"
import { Add, Business, Delete, Storefront } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import { BotaoVoltar } from "./BotaoVoltar"
import { DialogNovoEstabelecimento } from "./DialogNovoEstabelecimento"
import { EstabelecimentosAplicativoActionsTypes } from "../context/estabelecimentosAplicativoActions"
import { useEstabelecimentosAplicativoContext } from "../context/useEstabelecimentosAplicativoContext"
import type { EstabelecimentoOption } from "../models/estabelecimentosAplicativoModel"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    header: {
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down("sm")]: { marginBottom: theme.spacing(3) },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: {
            borderRadius: 15,
            marginBottom: theme.spacing(2),
        },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    addButton: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        padding: theme.spacing(1, 3),
        fontWeight: 600,
        boxShadow: "0 4px 15px rgba(var(--cor-botao-salvar-rgb), 0.3)",
        "&:hover": {
            backgroundColor: "var(--cor-botao-salvar-hover)",
            boxShadow: "0 6px 20px rgba(var(--cor-botao-salvar-rgb), 0.4)",
        },
    },
    addRow: {
        display: "flex",
        gap: theme.spacing(2),
        alignItems: "center",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "stretch",
        },
    },
    selectEstab: {
        flex: 1,
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    somenteLeituraAviso: {
        color: "#e65100",
        fontWeight: 500,
        marginBottom: theme.spacing(2),
    },
    emptyText: {
        color: "#7f8c8d",
        fontStyle: "italic",
    },
}))

export function FormularioEstabelecimentosAplicativo() {
    const classes = useStyles()
    const theme = useTheme()
    const isSmall = useMediaQuery(theme.breakpoints.down("sm"))
    const { dados, dispatch, adicionar, desassociar, criarNovo, erroCarregamento } =
        useEstabelecimentosAplicativoContext()
    const [dialogNovoAberto, setDialogNovoAberto] = useState(false)
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [erroCarregamento])

    const desabilitado = !!erroCarregamento || !dados.podeEditar

    const handleSelecionar = (_event: React.ChangeEvent<{}>, novo: EstabelecimentoOption | null) => {
        dispatch({
            type: EstabelecimentosAplicativoActionsTypes.SELECIONAR_NOVO,
            payload: novo ? novo.id : null,
        })
    }

    const estabelecimentoSelecionado =
        dados.todosEstabelecimentos.find(e => e.id === dados.idParaAdicionar) ?? null

    const handleAdicionar = async () => {
        if (!dados.idParaAdicionar) {
            alertStart("Selecione um estabelecimento para adicionar.", "error")
            return
        }
        const jaAdicionado = dados.estabelecimentosDoApp.some(
            e => e.id === dados.idParaAdicionar
        )
        if (jaAdicionado) {
            alertStart("Este estabelecimento já está associado ao aplicativo.", "error")
            return
        }
        try {
            await adicionar(dados.idParaAdicionar)
            alertStart("Estabelecimento adicionado com sucesso!", "success")
        } catch (err) {
            alertStart((err as Error).message || "Erro ao adicionar estabelecimento", "error")
        }
    }

    const handleDesassociar = async (idEstabelecimento: number, nome: string) => {
        if (!window.confirm(`Tem certeza que deseja desassociar "${nome}"? Esta ação não pode ser desfeita.`)) {
            return
        }
        try {
            await desassociar(idEstabelecimento)
            alertStart("Estabelecimento desassociado com sucesso!", "success")
        } catch (err) {
            alertStart((err as Error).message || "Erro ao desassociar estabelecimento", "error")
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <BotaoVoltar />
                <Box className={classes.header}>
                    <Typography variant={isSmall ? "h5" : "h4"} className={classes.titleText}>
                        Estabelecimentos do Aplicativo
                    </Typography>
                </Box>

                {dados.podeEditar === false && (
                    <Typography className={classes.somenteLeituraAviso}>
                        Este estabelecimento não possui um aplicativo próprio. Os dados são somente leitura.
                    </Typography>
                )}

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Storefront className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Novo Estabelecimento
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Typography className={classes.emptyText} style={{ marginBottom: 16 }}>
                            Cadastra uma nova loja (e o usuário responsável) já vinculada a este
                            aplicativo.
                        </Typography>
                        <Button
                            variant="contained"
                            className={classes.addButton}
                            startIcon={<Add />}
                            onClick={() => setDialogNovoAberto(true)}
                            disabled={desabilitado}
                        >
                            Novo estabelecimento
                        </Button>
                    </Box>
                </Paper>

                <DialogNovoEstabelecimento
                    aberto={dialogNovoAberto}
                    onFechar={() => setDialogNovoAberto(false)}
                    criar={criarNovo}
                    onCriado={estab =>
                        alertStart(
                            `Estabelecimento "${estab.nome}" cadastrado! Login: ${estab.loginResponsavel} (senha inicial: celular do responsável).`,
                            "success"
                        )
                    }
                    onErro={msg => alertStart(msg, "error")}
                />

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Business className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Associar Estabelecimento
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box className={classes.addRow}>
                            <Autocomplete
                                className={classes.selectEstab}
                                options={dados.todosEstabelecimentos}
                                getOptionLabel={opcao => opcao.nome}
                                getOptionSelected={(opcao, valor) => opcao.id === valor.id}
                                value={estabelecimentoSelecionado}
                                onChange={handleSelecionar}
                                disabled={desabilitado}
                                noOptionsText="Nenhum estabelecimento encontrado"
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        size="small"
                                        label="Selecione um estabelecimento"
                                    />
                                )}
                            />
                            <Button
                                variant="contained"
                                className={classes.addButton}
                                startIcon={<Add />}
                                onClick={handleAdicionar}
                                disabled={desabilitado || !dados.idParaAdicionar}
                            >
                                Adicionar
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Business className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Estabelecimentos Associados
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {dados.estabelecimentosDoApp.length === 0 ? (
                            <Typography className={classes.emptyText}>
                                Não existem estabelecimentos associados.
                            </Typography>
                        ) : (
                            <TableContainer style={{ overflowX: "auto" }}>
                                <Table
                                    size="small"
                                    stickyHeader
                                    style={{ minWidth: 380, width: "100%" }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ minWidth: 200 }}>Nome</TableCell>
                                            <TableCell style={{ minWidth: 100 }}>Visível</TableCell>
                                            <TableCell style={{ minWidth: 80 }} />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dados.estabelecimentosDoApp.map(estab => (
                                            <TableRow key={estab.id}>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={estab.nome}
                                                >
                                                    {estab.nome}
                                                </TableCell>
                                                <TableCell>
                                                    {estab.visivel ? "Sim" : "Não"}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    style={{ whiteSpace: "nowrap" }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        disabled={desabilitado}
                                                        onClick={() =>
                                                            handleDesassociar(estab.id, estab.nome)
                                                        }
                                                        title="Desassociar"
                                                    >
                                                        <Delete
                                                            fontSize="small"
                                                            style={{ color: desabilitado ? undefined : "var(--cor-erro)" }}
                                                        />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
